import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Archive, ArrowLeft, CheckCircle2, Clock3, Download, FileEdit,
  FileText, FolderArchive, Link2, Pencil, Plus, RotateCcw, Search, Upload,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { API, api, eur, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { openAttachmentViewer } from "@/lib/attachmentViewer";

const STATI = {
  bozza: { label: "Bozza", colore: "bg-slate-400", Icon: FileEdit },
  attesa: { label: "In attesa", colore: "bg-orange-500", Icon: Clock3 },
  accettato: { label: "Accettato", colore: "bg-emerald-500", Icon: CheckCircle2 },
  respinto: { label: "Respinto", colore: "bg-red-500", Icon: XCircle },
};

const formVuoto = () => ({
  data: new Date().toISOString().slice(0, 10), cliente: "", oggetto: "",
  importo: "", note: "", stato: "attesa", file: null,
});

const errore = (e) => formatApiError(e?.response?.data?.detail || e?.message);

function Stato({ stato }) {
  const conf = STATI[stato] || STATI.bozza;
  return <span className="inline-flex items-center gap-2 text-sm font-semibold">
    <span className={`h-2.5 w-2.5 rounded-full ${conf.colore}`} />{conf.label}
  </span>;
}

export default function PreventiviPage() {
  const navigate = useNavigate();
  const fileModello = useRef(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ bozza: 0, attesa: 0, accettato: 0, respinto: 0, archiviati: 0 });
  const [anni, setAnni] = useState([]);
  const [archivio, setArchivio] = useState(false);
  const [filtroStato, setFiltroStato] = useState("default");
  const [ricerca, setRicerca] = useState("");
  const annoCorrente = String(new Date().getFullYear());
  const [anno, setAnno] = useState(annoCorrente);
  const [modello, setModello] = useState({ configurato: false, nome: null });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [modelloOpen, setModelloOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(formVuoto());
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cantieri, setCantieri] = useState([]);
  const [collegaOpen, setCollegaOpen] = useState(false);
  const [preventivoDaCollegare, setPreventivoDaCollegare] = useState(null);
  const [cantiereScelto, setCantiereScelto] = useState("");

  const carica = async (inArchivio = archivio) => {
    const [elenco, info] = await Promise.all([
      api.get("/preventivi", { params: { archiviati: inArchivio } }),
      api.get("/preventivi/modello/info"),
    ]);
    setItems(elenco.data.items || []);
    setStats(elenco.data.stats || {});
    setAnni(elenco.data.anni || []);
    setModello(info.data);
  };

  useEffect(() => { carica().catch((e) => toast.error(errore(e))); }, [archivio]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibili = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    return items.filter((x) => {
      const statoOk = archivio || filtroStato === "default"
        ? (archivio || ["bozza", "attesa"].includes(x.stato))
        : x.stato === filtroStato;
      const annoOk = anno === "tutti" || String(x.data || "").startsWith(anno);
      const testo = [x.numero, x.cliente, x.oggetto, x.note, x.pdf_nome].join(" ").toLowerCase();
      return statoOk && annoOk && (!q || testo.includes(q));
    });
  }, [items, ricerca, anno, filtroStato, archivio]);

  const cambiaVista = (isArchivio) => {
    setArchivio(isArchivio); setFiltroStato("default"); setRicerca(""); setAnno(annoCorrente);
  };

  const salvaNuovo = async () => {
    if (!form.file || !form.cliente.trim()) return toast.error("Inserisci cliente e PDF del preventivo");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v == null ? "" : v));
    setBusy(true);
    try {
      await api.post("/preventivi", fd);
      toast.success("Preventivo caricato"); setUploadOpen(false); setForm(formVuoto()); await carica(false);
    } catch (e) { toast.error(errore(e)); } finally { setBusy(false); }
  };

  const apriModifica = (x) => {
    setEditing(x);
    setForm({ data: x.data, cliente: x.cliente, oggetto: x.oggetto || "", importo: x.importo || "", note: x.note || "", stato: x.stato, file: null });
    setEditOpen(true);
  };

  const salvaModifica = async () => {
    setBusy(true);
    try {
      await api.put(`/preventivi/${editing.id}`, { ...form, importo: Number(form.importo || 0), file: undefined });
      if (form.file) { const fd = new FormData(); fd.append("file", form.file); await api.post(`/preventivi/${editing.id}/pdf`, fd); }
      toast.success("Preventivo aggiornato"); setEditOpen(false); await carica();
    } catch (e) { toast.error(errore(e)); } finally { setBusy(false); }
  };

  const archivia = async (x) => {
    if (!window.confirm(`Archiviare il preventivo ${x.numero} con stato ${STATI[x.stato]?.label}?`)) return;
    try { await api.post(`/preventivi/${x.id}/archivia`); toast.success("Preventivo archiviato"); await carica(); }
    catch (e) { toast.error(errore(e)); }
  };

  const ripristina = async (x) => {
    try { await api.post(`/preventivi/${x.id}/ripristina`); toast.success("Preventivo ripristinato"); await carica(true); }
    catch (e) { toast.error(errore(e)); }
  };

  const apriCollegamento = async (preventivo) => {
    setPreventivoDaCollegare(preventivo);
    setCantiereScelto(preventivo.cantiere_id || "");
    setCollegaOpen(true);
    try {
      const risposta = await api.get("/cantieri");
      setCantieri(Array.isArray(risposta.data) ? risposta.data : []);
    } catch (e) {
      toast.error(errore(e));
    }
  };

  const collegaCantiere = async () => {
    if (!cantiereScelto) return toast.error("Seleziona un cantiere esistente");
    setBusy(true);
    try {
      await api.post(`/preventivi/${preventivoDaCollegare.id}/collega-cantiere`, { cantiere_id: cantiereScelto });
      toast.success("Preventivo collegato al cantiere");
      setCollegaOpen(false);
      await carica(true);
    } catch (e) { toast.error(errore(e)); } finally { setBusy(false); }
  };

  const salvaModello = async () => {
    const file = fileModello.current?.files?.[0];
    if (!file) return toast.error("Seleziona un file Word .doc o .docx");
    const fd = new FormData(); fd.append("file", file); setBusy(true);
    try { await api.post("/preventivi/modello", fd); toast.success("Carta intestata impostata"); setModelloOpen(false); await carica(); }
    catch (e) { toast.error(errore(e)); } finally { setBusy(false); }
  };

  const nuovoPreventivo = () => {
    if (!modello.configurato) { toast.error("Imposta prima la carta intestata Word"); setModelloOpen(true); return; }
    const url = `${window.location.origin}${API}/preventivi/modello/download`;
    const a = document.createElement("a"); a.href = url; a.download = modello.nome || "carta-intestata.docx";
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Modello Word scaricato. Aprilo dalla cartella Download.");
  };

  const cards = ["bozza", "attesa", "accettato", "respinto"];
  return <AppShell>
    <button onClick={() => navigate("/gestionale")} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary mb-6"><ArrowLeft className="h-4 w-4" />Gestionale</button>
    <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-5 mb-7">
      <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Gestione economica</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mt-2">{archivio ? "Archivio preventivi" : "Preventivi"}</h1>
        <p className="text-muted-foreground mt-2">Carica, controlla e archivia i preventivi preparati con la tua carta intestata.</p></div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setModelloOpen(true)}><FileText className="h-4 w-4 mr-2" />Imposta carta intestata</Button>
        <Button variant="outline" onClick={() => cambiaVista(!archivio)}>{archivio ? <RotateCcw className="h-4 w-4 mr-2" /> : <FolderArchive className="h-4 w-4 mr-2" />}{archivio ? "Torna ai preventivi" : `Preventivi archiviati (${stats.archiviati || 0})`}</Button>
        {!archivio && <><Button variant="outline" onClick={() => { setForm(formVuoto()); setUploadOpen(true); }}><Upload className="h-4 w-4 mr-2" />Carica preventivo PDF</Button>
          <Button onClick={nuovoPreventivo}><Plus className="h-4 w-4 mr-2" />Nuovo preventivo</Button></>}
      </div>
    </div>

    {!archivio && <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
      {cards.map((stato) => { const conf = STATI[stato]; const Icon = conf.Icon; return <button key={stato} onClick={() => setFiltroStato(stato)} className={`rounded-xl border bg-card p-5 text-left transition-all hover:border-primary ${filtroStato === stato ? "ring-2 ring-primary border-primary" : ""}`}>
        <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground font-semibold">{conf.label}</span><Icon className="h-5 w-5 text-muted-foreground" /></div>
        <p className="font-heading text-3xl font-bold mt-2">{stats[stato] || 0}</p>
      </button>; })}
    </div>}

    <section className="rounded-xl border bg-card overflow-hidden">
      <div className="p-4 border-b flex flex-col md:flex-row gap-3">
        <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input className="pl-9" value={ricerca} onChange={(e) => setRicerca(e.target.value)} placeholder="Cerca numero, cliente, oggetto o note..." /></div>
        <Select value={anno} onValueChange={setAnno}><SelectTrigger className="md:w-44"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="tutti">Tutti gli anni</SelectItem>{[...new Set([annoCorrente, ...anni])].sort().reverse().map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
        {!archivio && filtroStato !== "default" && <Button variant="ghost" onClick={() => setFiltroStato("default")}>Mostra Bozze + In attesa</Button>}
      </div>
      <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Data</TableHead><TableHead>N. preventivo</TableHead><TableHead>Cliente</TableHead><TableHead>Oggetto</TableHead><TableHead>Stato</TableHead><TableHead>Cantiere</TableHead><TableHead className="text-right">Importo</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
        <TableBody>{visibili.length ? visibili.map((x) => <TableRow key={x.id}><TableCell>{x.data?.split("-").reverse().join("/")}</TableCell><TableCell className="font-semibold">{x.numero}</TableCell><TableCell>{x.cliente}</TableCell><TableCell>{x.oggetto || "—"}</TableCell><TableCell><Stato stato={x.stato} /></TableCell><TableCell>{x.cantiere_id ? <button className="font-semibold text-primary hover:underline" onClick={() => navigate(`/cantieri/${x.cantiere_id}`)}>{x.cantiere_nome || "Apri cantiere"}</button> : "—"}</TableCell><TableCell className="text-right font-semibold">{eur(x.importo)}</TableCell><TableCell><div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => openAttachmentViewer(`${API}/preventivi/${x.id}/pdf`, `Preventivo ${x.numero}`)}><Download className="h-4 w-4 mr-1" />PDF</Button>
          {!archivio && <Button size="sm" variant="ghost" onClick={() => apriModifica(x)}><Pencil className="h-4 w-4 mr-1" />Modifica</Button>}
          {!archivio && ["accettato", "respinto"].includes(x.stato) && <Button size="sm" variant="ghost" onClick={() => archivia(x)}><Archive className="h-4 w-4 mr-1" />Archivia</Button>}
          {archivio && x.stato === "accettato" && <Button size="sm" variant="ghost" onClick={() => apriCollegamento(x)}><Link2 className="h-4 w-4 mr-1" />{x.cantiere_id ? "Cambia cantiere" : "Collega a cantiere"}</Button>}
          {archivio && <Button size="sm" variant="ghost" onClick={() => ripristina(x)}><RotateCcw className="h-4 w-4 mr-1" />Ripristina</Button>}
        </div></TableCell></TableRow>) : <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-12">Nessun preventivo da visualizzare.</TableCell></TableRow>}</TableBody>
      </Table></div>
    </section>

    <PreventivoDialog open={uploadOpen} onOpenChange={setUploadOpen} title="Carica preventivo PDF" form={form} setForm={setForm} busy={busy} onSave={salvaNuovo} richiediPdf />
    <PreventivoDialog open={editOpen} onOpenChange={setEditOpen} title={`Modifica ${editing?.numero || "preventivo"}`} form={form} setForm={setForm} busy={busy} onSave={salvaModifica} />
    <Dialog open={collegaOpen} onOpenChange={setCollegaOpen}><DialogContent><DialogHeader><DialogTitle>Collega preventivo a cantiere</DialogTitle><DialogDescription>Seleziona il cantiere esistente da associare al preventivo {preventivoDaCollegare?.numero}.</DialogDescription></DialogHeader>
      <div><Label>Cantiere *</Label><SearchableCombobox className="mt-2" value={cantiereScelto} onValueChange={setCantiereScelto} options={cantieri.map((c) => ({ value: c.id, label: c.nome, keywords: `${c.cliente || ""} ${c.indirizzo || ""}` }))} placeholder="Seleziona il cantiere" searchPlaceholder="Cerca nome, cliente o indirizzo..." emptyText="Nessun cantiere trovato" /></div>
      <DialogFooter><Button variant="outline" onClick={() => setCollegaOpen(false)}>Annulla</Button><Button disabled={busy || !cantiereScelto} onClick={collegaCantiere}>{busy ? "Collegamento..." : "Collega cantiere"}</Button></DialogFooter>
    </DialogContent></Dialog>
    <Dialog open={modelloOpen} onOpenChange={setModelloOpen}><DialogContent><DialogHeader><DialogTitle>Imposta carta intestata</DialogTitle><DialogDescription>Carica il modello Word da usare come base per i nuovi preventivi.</DialogDescription></DialogHeader>
      {modello.configurato && <div className="rounded-lg bg-muted p-3 text-sm">Modello attuale: <strong>{modello.nome}</strong></div>}
      <div><Label>File Word (.doc o .docx)</Label><Input ref={fileModello} className="mt-2" type="file" accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" /></div>
      <DialogFooter><Button variant="outline" onClick={() => setModelloOpen(false)}>Annulla</Button><Button disabled={busy} onClick={salvaModello}>Salva carta intestata</Button></DialogFooter>
    </DialogContent></Dialog>
  </AppShell>;
}

function PreventivoDialog({ open, onOpenChange, title, form, setForm, busy, onSave, richiediPdf = false }) {
  const cambia = (campo, valore) => setForm((x) => ({ ...x, [campo]: valore }));
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="sm:max-w-2xl"><DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>Inserisci i dati identificativi e scegli lo stato del preventivo.</DialogDescription></DialogHeader>
    <div className="grid sm:grid-cols-2 gap-4">
      <div><Label>Data</Label><Input className="mt-2" type="date" value={form.data} onChange={(e) => cambia("data", e.target.value)} /></div>
      <div><Label>Stato</Label><Select value={form.stato} onValueChange={(v) => cambia("stato", v)}><SelectTrigger className="mt-2"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(STATI).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select></div>
      <div><Label>Cliente</Label><Input className="mt-2" value={form.cliente} onChange={(e) => cambia("cliente", e.target.value)} placeholder="Nome cliente" /></div>
      <div><Label>Importo €</Label><Input className="mt-2" type="number" min="0" step="0.01" value={form.importo} onChange={(e) => cambia("importo", e.target.value)} placeholder="0,00" /></div>
      <div className="sm:col-span-2"><Label>Oggetto</Label><Input className="mt-2" value={form.oggetto} onChange={(e) => cambia("oggetto", e.target.value)} placeholder="Oggetto del preventivo" /></div>
      <div className="sm:col-span-2"><Label>Note</Label><Textarea className="mt-2" value={form.note} onChange={(e) => cambia("note", e.target.value)} /></div>
      <div className="sm:col-span-2"><Label>{richiediPdf ? "Preventivo PDF" : "Sostituisci PDF (facoltativo)"}</Label><Input className="mt-2" type="file" accept="application/pdf,.pdf" onChange={(e) => cambia("file", e.target.files?.[0] || null)} /></div>
    </div>
    <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button><Button disabled={busy} onClick={onSave}>{busy ? "Salvataggio..." : "Salva"}</Button></DialogFooter>
  </DialogContent></Dialog>;
}
